/* ============================================================
   MOSAIK — TEMPLATE LIBRARY

   A curated set of ready-made designs the visitor can browse and apply.
   Applying a template drops its collection / grid / tiles / borders /
   corners (and, when the lights feature is live, its cable) onto the
   canvas, after which the visitor keeps editing freely.

   Two parts below:
     1. DATA  — window.MOSAIK_TEMPLATES : the library. Hand-edit to curate.
     2. LOGIC — window.MosaikTemplates  : gallery UI + apply + previews.

   Templates are DIMENSION-AGNOSTIC: applying one keeps the visitor's current
   width/length and regenerates the design to fit that grid. A template never
   forces its own size — it only refuses to apply when the current grid is
   smaller than the motif needs (minCols / minRows).

   A template shape:
     {
       id, name_fr, name_en,
       collection : 'Polypropylene' | 'PVC' | ...   (key in MOSAIK_COLLECTIONS)
       pattern    : (r, c, cols, rows) => pileIndex | null   // motif generator,
                    // evaluated against the LIVE grid so it adapts to any size
       minCols, minRows : smallest grid the motif needs      (optional, default 1)
       previewCols, previewRows : grid used only for the gallery thumbnail
                    (optional, default 6x4)
       borderAll  : border index applied to every edge      (optional)
       borders    : { tileEdgeIdx: {b:borderIndex, side} }  (optional, explicit)
       cornerAll  : corner index applied to all 4 corners    (optional)
       corners    : { cornerIdx(0..3): cornerIndex }         (optional, explicit)
       lights     : serialized cable (optional; applied only when lights are live)
     }

   Legacy shape (still accepted): a fixed `tiles` array with `cols`/`rows`. It is
   tiled (repeated) across the live grid so even snapshots stay size-agnostic.

   Authoring tip: build a design in the UI, then run  MosaikTemplates.exportCurrent()
   in the browser console to get a ready-to-paste template object.
   ============================================================ */
(function(){

  /* -------- motif helpers (shared by the patterns below) --------
     Polypropylene pile indices: 0=Noir 1=Gris 2=Rouge 3=Jaune 4=Bleu 5=Blanc. */

  /* alternating checkerboard of two pile indices */
  const checker = (r, c, a, b) => ((r + c) & 1) ? b : a;

  /* true on the 1-tile outline of the rectangle [r0..r1] x [c0..c1] */
  const frame = (r, c, r0, r1, c0, c1) =>
    r >= r0 && r <= r1 && c >= c0 && c <= c1 &&
    (r === r0 || r === r1 || c === c0 || c === c1);

  /* proportional index into `n` cells (keeps motifs adaptive to any grid) */
  const at = (frac, n) => Math.round(frac * (n - 1));

  /* true on the CORNER BRACKETS of the rectangle [r0..r1] x [c0..c1] : la case
     du coin plus (len-1) cases le long de chacun de ses deux côtés. C'est le
     marquage d'emplacement au sol — les quatre équerres, sans les côtés. */
  const brackets = (r, c, r0, r1, c0, c1, len) => {
    if(r < r0 || r > r1 || c < c0 || c > c1) return false;
    const onR = (r === r0 || r === r1), onC = (c === c0 || c === c1);
    const nearC = (c - c0 < len) || (c1 - c < len);
    const nearR = (r - r0 < len) || (r1 - r < len);
    return (onR && nearC) || (onC && nearR);
  };

  /* true dans la bande de `n` dalles le long des quatre bords de la grille */
  const band = (r, c, cols, rows, n) =>
    r < n || r >= rows - n || c < n || c >= cols - n;

  /* -------- DATA: the shipped library (edit freely) --------
     Adaptive renditions of the 11 curated garage-floor designs. Every motif is a
     `pattern(r, c, cols, rows)` computed against the LIVE grid, so it fills any
     dimensions the visitor set (min sizes below guard the "too small" case).
     Irregular one-off details (staircases, notches, black blocks) are dropped in
     favour of the clean repeating/edge-relative motif each design is built on.
     NOTE: no `lights` field on any shipped template — the lights feature is
     gated off, so nothing here should hint at it. */
  window.MOSAIK_TEMPLATES = [  
    {
      /* Ligne centrale — trois bandes verticales (bleu ciel / bleu / rouge) sur
         fond blanc. Le tableau figé d'origine (16×21) est ici remis en `pattern` :
         il donnait exactement ce dessin à 16 colonnes, mais sa vignette de galerie
         sortait vide (l'aperçu n'échantillonne que le coin haut-gauche du tableau)
         et les bandes se seraient répétées sur une grille plus large. */
      id:'poly-ligne-centrale',
      name_fr:'Ligne centrale', name_en:'Central line',
      collection:'Polypropylene',
      minCols:4, minRows:3, previewCols:12, previewRows:15,
      pattern:(r, c, cols) => {
        const c0 = at(0.40, cols);                         // 6 sur une grille de 16
        return [7, 4, 2][c - c0] != null ? [7, 4, 2][c - c0] : 5;
      }
    },

    /* Les cinq motifs ci-dessous viennent de plans dessinés dans l'UI en 16×21
       (export `MosaikTemplates.exportCurrent()`), retranscrits en `pattern` :
       ce sont des motifs CENTRÉS ou RELATIFS AUX BORDS, qu'un tableau `tiles`
       figé aurait répété au lieu de les remettre à l'échelle de la grille du
       visiteur (cadre coupé en deux, équerres dupliquées…). Les proportions
       reprennent celles du plan d'origine. */
    {
      // 2) marquage d'emplacement : quatre équerres rouges sur fond noir
      id:'poly-coins-emplacement',
      name_fr:'Coins d\'emplacement', name_en:'Parking spot corners',
      collection:'Polypropylene',
      minCols:6, minRows:8, previewCols:12, previewRows:15,
      pattern:(r, c, cols, rows) =>
        brackets(r, c, at(0.25, rows), at(0.80, rows), at(0.27, cols), at(0.67, cols), 2) ? 2 : 0
    },
    {
      // 3) trois travées : deux lignes grises sur fond blanc
      id:'poly-lignes-separation',
      name_fr:'Lignes de séparation', name_en:'Bay divider lines',
      collection:'Polypropylene',
      minCols:5, minRows:3, previewCols:12, previewRows:15,
      pattern:(r, c, cols) =>
        (c === at(1 / 3, cols) || c === at(2 / 3, cols)) ? 1 : 5   // 1=Gris, 5=Blanc
    },
    {
      // 4) cadre rouge d'une dalle, en retrait de deux dalles des bords
      id:'poly-cadre-rouge',
      name_fr:'Cadre rouge', name_en:'Red outline',
      collection:'Polypropylene',
      minCols:7, minRows:7, previewCols:12, previewRows:15,
      pattern:(r, c, cols, rows) => frame(r, c, 2, rows - 3, 2, cols - 3) ? 2 : 0
    },
    {
      // 5) bordure rouge épaisse (deux dalles) tout autour
      id:'poly-bordure-large',
      name_fr:'Bordure large', name_en:'Wide border',
      collection:'Polypropylene',
      minCols:6, minRows:6, previewCols:12, previewRows:15,
      pattern:(r, c, cols, rows) => band(r, c, cols, rows, 2) ? 2 : 0
    },
    {
      // 6) damier noir & blanc enfermé dans un cadre rouge, sur fond noir
      id:'poly-damier-encadre',
      name_fr:'Damier encadré', name_en:'Framed checkerboard',
      collection:'Polypropylene',
      minCols:7, minRows:8, previewCols:12, previewRows:15,
      pattern:(r, c, cols, rows) => {
        const r0 = at(0.20, rows), r1 = at(0.75, rows),
              c0 = at(0.27, cols), c1 = at(0.67, cols);
        if(frame(r, c, r0, r1, c0, c1)) return 2;
        if(r > r0 && r < r1 && c > c0 && c < c1) return checker(r, c, 5, 0);
        return 0;
      }
    }
  ];

  /* -------- LOGIC -------- */
  window.MosaikTemplates = {

    /* Grid the LIVE configurator currently shows for a given collection —
       derived from the visitor's width/length, never from the template. */
    liveGridOf:function(col){
      const w = parseFloat(document.getElementById('mosaik-area-width').value)  || 0;
      const h = parseFloat(document.getElementById('mosaik-area-height').value) || 0;
      return { cols: Math.ceil(w / (col.width || 1)), rows: Math.ceil(h / (col.height || 1)) };
    },

    /* Grid used only for the gallery thumbnail (templates carry no real size). */
    previewGridOf:function(tpl){
      return { cols: tpl.previewCols || 6, rows: tpl.previewRows || 4 };
    },

    /* Resolve a template's motif into a row-major pile-index array for an
       arbitrary cols x rows grid. Supports the `pattern` function shape and the
       legacy fixed `tiles` array (repeated to fill the grid). */
    resolveTiles:function(tpl, cols, rows){
      const out = [];
      if(typeof tpl.pattern === 'function'){
        for(let r = 0; r < rows; r++) for(let c = 0; c < cols; c++) out.push(tpl.pattern(r, c, cols, rows));
      } else if(tpl.tiles && tpl.cols && tpl.rows){
        for(let r = 0; r < rows; r++) for(let c = 0; c < cols; c++)
          out.push(tpl.tiles[(r % tpl.rows) * tpl.cols + (c % tpl.cols)]);
      }
      return out;
    },

    /* Small idx+side-only layout so we can reuse the real border geometry
       without any pixel maths (only rows/cols drive idx & side). */
    _geomStub:function(cols, rows){
      return { cols, rows, tileW:1, tileH:1, bW:1, bH:1, xOff:0, yOff:0, cW:cols+2, cH:rows+2 };
    },

    /* ---- apply a template to the live configurator ---- */
    apply:function(tpl){
      const m = window.mosaik;
      const col = m.collections[tpl.collection];
      if(!col){ console.warn('[MosaikTemplates] unknown collection:', tpl.collection); return; }

      // Keep the visitor's current dimensions. The grid is whatever those
      // dimensions produce with this template's collection tile size — the
      // template imposes no width/length of its own.
      const { cols, rows } = this.liveGridOf(col);

      // refuse only when the current area is too small for the motif
      if(cols < (tpl.minCols || 1) || rows < (tpl.minRows || 1)){
        window.alert(m.getText('Your dimensions are too small for this template.'));
        return;
      }

      // guard against silently wiping an in-progress design
      const hasDesign = (m.result && m.result.some(Boolean))
                     || Object.keys(m.borderAssignments).length
                     || Object.keys(m.cornerAssignments).length;
      if(hasDesign && !window.confirm(m.getText('Replace the current design?'))) return;

      // 1) collection only — dimensions (width/height inputs) are left untouched
      const sel = document.getElementById('mosaik-collection');
      if(sel) sel.value = tpl.collection;

      // 2) reset + rebuild collection UI (mirrors the collection-change handler)
      m.options.checkerboard_piles = [];
      m.result = [];
      m.borderAssignments = {};
      m.cornerAssignments = {};
      if(window.MosaikLights) MosaikLights.clear(m);
      if(window.MosaikObjects) MosaikObjects.clear(m);
      m.setCollection();
      if(window.MosaikObjects) MosaikObjects.buildColors();
      const atc = document.getElementById('mosaik-add-to-cart');
      if(atc) atc.style.display = (tpl.collection === 'Polypropylene') ? 'inline' : 'none';

      // 3) tiles — generated to fit the live grid
      const piles = col.piles;
      m.result = [];
      this.resolveTiles(tpl, cols, rows).forEach((p, idx) => {
        if(p === -1) m.result[idx] = false;             // dalle supprimée
        else if(p != null && piles[p]) m.result[idx] = piles[p];
      });

      // 4) borders (whole-frame shorthand + explicit map)
      if(tpl.borderAll != null && col.borders[tpl.borderAll]){
        MosaikGeometry.buildBorderCells(this._geomStub(cols, rows)).forEach(bc => {
          m.borderAssignments[bc.idx] = { ...col.borders[tpl.borderAll], side: bc.side };
        });
      }
      Object.entries(tpl.borders || {}).forEach(([idx, b]) => {
        const bo = col.borders[b.b];
        if(bo) m.borderAssignments[idx] = { ...bo, side: b.side };
      });

      // 5) corners (all-four shorthand + explicit map)
      if(tpl.cornerAll != null && col.corners[tpl.cornerAll]){
        [0, 1, 2, 3].forEach(i => { m.cornerAssignments[i] = col.corners[tpl.cornerAll]; });
      }
      Object.entries(tpl.corners || {}).forEach(([idx, c]) => {
        const co = col.corners[c];
        if(co) m.cornerAssignments[idx] = co;
      });

      // 6) lights — only when the feature is actually live
      if(tpl.lights && tpl.lights.length && window.MosaikLights && MosaikLights.enabled){
        MosaikLights.loadChain(m, tpl.lights, cols);
        MosaikLights.updateStatus(m);
      }

      // 7) objets posés (emplacements véhicule, accessoires) — idem
      if(tpl.objects && tpl.objects.length && window.MosaikObjects && MosaikObjects.enabled)
        MosaikObjects.load(m, tpl.objects);

      m.draw();

      /* Le modèle remplace le dessin en cours (le visiteur vient de le
         confirmer) et peut changer de collection : les entrées précédentes
         portent des dalles d'un autre catalogue, elles ne sont plus rejouables.
         L'historique repart donc du modèle, comme après un changement de
         collection ou de dimensions. */
      if(window.MosaikHistory) MosaikHistory.resetHistory();
    },

    /* ---- gallery thumbnail: color-swatch mini-map straight from the data ---- */
    renderThumb:function(cv, tpl){
      const m = window.mosaik, col = m.collections[tpl.collection];
      if(!col) return;
      const { cols, rows } = this.previewGridOf(tpl);
      const tp = Math.max(4, Math.floor(150 / Math.max(cols, rows)));
      const mg = Math.max(2, Math.floor(tp / 3));
      cv.width = cols * tp + 2 * mg; cv.height = rows * tp + 2 * mg;
      const ctx = cv.getContext('2d');
      ctx.clearRect(0, 0, cv.width, cv.height);
      const piles = col.piles;
      const preview = this.resolveTiles(tpl, cols, rows);
      // tiles
      for(let r = 0; r < rows; r++) for(let c = 0; c < cols; c++){
        const p = preview[r * cols + c];
        ctx.fillStyle = (p != null && piles[p]) ? piles[p].color : '#eee';
        ctx.fillRect(mg + c * tp, mg + r * tp, tp, tp);
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 1;
        ctx.strokeRect(mg + c * tp, mg + r * tp, tp, tp);
      }
      // border frame
      if(tpl.borderAll != null && col.borders[tpl.borderAll]){
        ctx.fillStyle = col.borders[tpl.borderAll].color || '#333';
        ctx.fillRect(0, 0, cv.width, mg);
        ctx.fillRect(0, cv.height - mg, cv.width, mg);
        ctx.fillRect(0, 0, mg, cv.height);
        ctx.fillRect(cv.width - mg, 0, mg, cv.height);
      }
      // corners
      if(tpl.cornerAll != null && col.corners[tpl.cornerAll]){
        ctx.fillStyle = col.corners[tpl.cornerAll].color || '#333';
        ctx.fillRect(0, 0, mg, mg);
        ctx.fillRect(cv.width - mg, 0, mg, mg);
        ctx.fillRect(0, cv.height - mg, mg, mg);
        ctx.fillRect(cv.width - mg, cv.height - mg, mg, mg);
      }
      // light dots — only when the feature is live (never leak it otherwise)
      if(tpl.lights && tpl.lights.length && window.MosaikLights && MosaikLights.enabled){
        tpl.lights.forEach(n => {
          ctx.beginPath();
          ctx.arc(mg + n.col * tp + tp / 2, mg + n.row * tp + tp / 2, Math.max(1.5, tp / 6), 0, Math.PI * 2);
          ctx.fillStyle = n.product === 'extension' ? '#9aa0a6' : '#ffd21e';
          ctx.fill();
        });
      }
    },

    /* ---- UI wiring (called from bootstrap) ----
       La bibliothèque n'est plus un popup mais une carte de la barre latérale
       (voir le .liquid) : on monte les vignettes dans #mosaik-templates-grid,
       point. Rien à ouvrir ni à fermer — la carte se replie comme les autres.
       Les vignettes empruntent l'habillage de « Mes projets » (.mosaik-proj*) :
       les deux listes se lisent de la même façon et se suivent dans la page. */
    setupUI:function(mosaik){
      const grid = document.getElementById('mosaik-templates-grid');
      if(!grid) return; // markup not emitted (feature gated off server-side)
      const t = s => mosaik.getText(s);
      const self = this;

      grid.innerHTML = '';
      (window.MOSAIK_TEMPLATES || []).forEach(tpl => {
        const card = document.createElement('div');
        card.className = 'mosaik-proj';

        const cv = document.createElement('canvas');
        cv.title = t('Apply this design');
        card.appendChild(cv);

        const name = document.createElement('div');
        name.className = 'mosaik-proj-name';
        name.innerText = tpl['name_' + mosaik.options.lang] || tpl.name_fr || tpl.id;
        card.appendChild(name);

        const dims = document.createElement('div');
        dims.className = 'mosaik-proj-dims';
        dims.innerText = t('Any size');
        card.appendChild(dims);

        self.renderThumb(cv, tpl);
        card.addEventListener('click', () => self.apply(tpl));
        grid.appendChild(card);
      });
    },

    /* ---- authoring helper: dump the current design as a template object ----
       Produces a legacy fixed `tiles` snapshot (cols/rows). It stays size-agnostic
       on apply (the motif is repeated to fill the live grid). For a clean tiling
       result, prefer hand-writing a `pattern(r,c,cols,rows)` in the DATA block. */
    exportCurrent:function(){
      const m = window.mosaik, colKey = m.options.currentCollection, col = m.collections[colKey];
      const cols = Math.ceil(m.options.width  / m.options.mosaikWidth);
      const rows = Math.ceil(m.options.height / m.options.mosaikHeight);
      const piles = col.piles;
      const tilesOut = [];
      // -1 = dalle supprimée à la gomme, null = case laissée au fond (même
      // convention que MosaikMyTemplates.serialize)
      for(let i = 0; i < cols * rows; i++){
        const c = m.result[i];
        tilesOut.push(c ? piles.indexOf(c) : (c === false ? -1 : null));
      }
      const findIdx = (arr, o) => arr.findIndex(x => x.color === o.color && x.name_fr === o.name_fr);
      const borders = {};
      Object.entries(m.borderAssignments).forEach(([idx, bo]) => { borders[idx] = { b: findIdx(col.borders, bo), side: bo.side }; });
      const corners = {};
      Object.entries(m.cornerAssignments).forEach(([idx, co]) => { corners[idx] = findIdx(col.corners, co); });
      const lights = (window.MosaikLights && MosaikLights.enabled) ? MosaikLights.serializeChain() : null;
      const objects = (window.MosaikObjects && MosaikObjects.enabled) ? MosaikObjects.serialize() : null;
      const out = { id: 'custom', name_fr: 'Mon modèle', name_en: 'My template',
                    collection: colKey, cols, rows, tiles: tilesOut, borders, corners, lights, objects };
      console.log(JSON.stringify(out));
      return out;
    }
  };

})();
